import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, Mail, Phone } from "lucide-react";
import { BackLink } from "../components/BackLink.js";
import { HELP_FAQ } from "../lib/help-content.js";
import { SUPPORT_CONTACTS, telHref, type SupportContact } from "../lib/support-contacts.js";
import { PROFIL_CARD } from "../lib/profil-theme.js";

export const Route = createFileRoute("/profil_/aide")({
  component: HelpPage,
});

function HelpPage() {
  const primary = SUPPORT_CONTACTS.find((c) => c.primary) ?? SUPPORT_CONTACTS[0]!;
  const others = SUPPORT_CONTACTS.filter((c) => c !== primary);

  return (
    <div className="flex flex-1 flex-col gap-6 pb-2">
      <BackLink to="/profil">Profil</BackLink>

      <header>
        <h1 className="text-2xl font-semibold text-foreground">Centre d'aide</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Questions fréquentes et contacts Greenkub.
        </p>
      </header>

      {/* FAQ — <details> natif, chevron animé en CSS */}
      <section className={"overflow-hidden rounded-xl " + PROFIL_CARD}>
        <ul className="divide-y divide-border">
          {HELP_FAQ.map((item) => (
            <li key={item.question}>
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 text-sm font-medium text-foreground outline-none focus-visible:bg-background/60 [&::-webkit-details-marker]:hidden">
                  {item.question}
                  <ChevronDown
                    className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out group-open:rotate-180 motion-reduce:transition-none"
                    aria-hidden
                  />
                </summary>
                <p className="px-4 pb-4 text-sm leading-relaxed text-muted-foreground">
                  {item.answer}
                </p>
              </details>
            </li>
          ))}
        </ul>
      </section>

      {/* Contacts */}
      <section className="space-y-3">
        <h2 className="text-base font-medium text-foreground">Vos contacts</h2>
        <PrimaryContactCard contact={primary} />
        <ul className={"divide-y divide-border overflow-hidden rounded-xl " + PROFIL_CARD}>
          {others.map((c) => (
            <ContactRow key={c.email} contact={c} />
          ))}
        </ul>
      </section>
    </div>
  );
}

function PrimaryContactCard({ contact }: { contact: SupportContact }) {
  return (
    <div className={"space-y-3 rounded-xl p-4 " + PROFIL_CARD}>
      <div>
        <p className="text-base font-semibold text-foreground">{contact.name}</p>
        <p className="text-sm text-muted-foreground">{contact.role}</p>
      </div>
      <div className="flex gap-2">
        <a
          href={telHref(contact.phone)}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground outline-none transition-transform duration-100 ease-out active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none motion-reduce:active:scale-100"
        >
          <Phone className="size-4" aria-hidden />
          {contact.phone}
        </a>
        <a
          href={`mailto:${contact.email}`}
          aria-label={`Écrire à ${contact.name}`}
          className="flex size-11 items-center justify-center rounded-lg border border-border bg-background text-foreground outline-none transition-colors hover:border-primary/50 focus-visible:border-primary"
        >
          <Mail className="size-4" aria-hidden />
        </a>
      </div>
    </div>
  );
}

function ContactRow({ contact }: { contact: SupportContact }) {
  return (
    <li className="flex items-center gap-3 bg-background px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{contact.name}</p>
        <p className="truncate text-xs text-muted-foreground">{contact.role}</p>
      </div>
      <a
        href={telHref(contact.phone)}
        aria-label={`Appeler ${contact.name}`}
        className="flex size-9 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:bg-muted"
      >
        <Phone className="size-4" aria-hidden />
      </a>
      <a
        href={`mailto:${contact.email}`}
        aria-label={`Écrire à ${contact.name}`}
        className="flex size-9 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:bg-muted"
      >
        <Mail className="size-4" aria-hidden />
      </a>
    </li>
  );
}
